import { newRelic } from '~/utils/newRelic/newRelic';

import { SCAN_ACTIONS } from '../constants/scanActions.const';
import { createScanActionFailureScanAction } from '../scanActions/scanActionFailure.action';
import {
  type CreateScanActionHandlerOptions,
  type ScanAction,
  type ScanActionHandler,
  type ScanActionResult,
} from '../types/scanAction.type';
import { type ScanEvent } from '../types/scanEvent.type';
import {
  createScanActionExecutionError,
  createScanActionResolutionError,
} from './scanActionError.util';

/** The scan context worth reporting on. Never carries the scanned value itself. */
const scanAttributes = ({ correlationId, scanContext }: ScanEvent) => ({
  correlationId,
  hasBarcodeContent: Boolean(scanContext.barcodeContent),
  openDrawerName: scanContext.ui.openDrawerName,
  route: scanContext.route.route,
});

export const createScanActionHandler = ({
  failureAction = createScanActionFailureScanAction(),
  scanActions = SCAN_ACTIONS,
}: CreateScanActionHandlerOptions = {}): ScanActionHandler => {
  const executeFailureScanAction = (
    description: string,
    error: Error,
    scanEvent: ScanEvent,
    customAttributes: Record<string, unknown> = {},
  ): ScanActionResult => {
    newRelic.noticeError(description, { ...scanAttributes(scanEvent), ...customAttributes, error });

    try {
      return failureAction.execute(scanEvent, error);
    } catch (cause) {
      const failureError = createScanActionExecutionError(
        failureAction.name,
        scanEvent.correlationId,
        cause,
      );
      newRelic.noticeError('Failure: Failed to execute failure action', {
        ...scanAttributes(scanEvent),
        error: failureError,
      });

      return { error: failureError, success: false };
    }
  };

  const executeScanAction = (scanAction: ScanAction, scanEvent: ScanEvent): ScanActionResult => {
    newRelic.log('Scan action resolved', {
      customAttributes: { actionName: scanAction.name, correlationId: scanEvent.correlationId },
      level: 'DEBUG',
    });

    try {
      return scanAction.execute(scanEvent);
    } catch (cause) {
      return executeFailureScanAction(
        'Failure: Failed to execute a scan action',
        createScanActionExecutionError(scanAction.name, scanEvent.correlationId, cause),
        scanEvent,
        { actionName: scanAction.name },
      );
    }
  };

  return {
    handle: (scanEvent) => {
      try {
        const scanAction = scanActions.find((action) => action.matches(scanEvent));

        if (!scanAction) {
          return executeFailureScanAction(
            'Failure: No scan action found',
            createScanActionResolutionError(scanEvent.correlationId),
            scanEvent,
          );
        }

        return executeScanAction(scanAction, scanEvent);
      } catch (cause) {
        return executeFailureScanAction(
          'Failure: Failed to execute scan action',
          createScanActionResolutionError(scanEvent.correlationId, cause),
          scanEvent,
        );
      }
    },
  };
};
